import { LayoutMain } from 'layouts/main';
import type { RouteObject } from 'react-router-dom';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import {
    InvoicesSubRoutes,
    MainRoutes,
    MerchantsSubRoutes,
} from 'utils/constants/routes';
import { routesHelper } from 'utils/helpers/routesHelper';

import { ErrorBoundary } from 'components/ui/ErrorBoundary';
import { AsyncPage } from 'components/utils/AsyncPage/AsyncPage';
import { ProtectedPage } from 'components/utils/ProtectedPage';

import { Pages } from 'models/Pages';

const balanceListRoute = routesHelper.join(
    MainRoutes.merchants,
    MerchantsSubRoutes.Balance,
    MerchantsSubRoutes.List,
);

type TRouteWithErrorBoundary = RouteObject & {
    errorElement: React.ReactNode;
};

const createRouteWithErrorBoundary = (
    route: RouteObject,
): TRouteWithErrorBoundary => {
    const routeWithErrorBoundary = {
        ...route,
        errorElement: route.errorElement || <ErrorBoundary />,
    };

    if (route.children) {
        routeWithErrorBoundary.children = route.children.map(
            createRouteWithErrorBoundary,
        );
    }

    return routeWithErrorBoundary;
};

const router = createBrowserRouter([
    createRouteWithErrorBoundary({
        path: MainRoutes.home,
        element: (
            <ProtectedPage>
                <LayoutMain />
            </ProtectedPage>
        ),
        children: [
            {
                path: routesHelper.getSubThreeRoute(MainRoutes.merchants),
                children: [
                    {
                        path: routesHelper.getSubThreeRoute(
                            MerchantsSubRoutes.Balance,
                        ),
                        element: <AsyncPage page={Pages.Balances} />,
                    },
                ],
            },
            {
                path: routesHelper.getSubThreeRoute(MainRoutes.invoices),
                children: [
                    {
                        path: routesHelper.getSubThreeRoute(
                            InvoicesSubRoutes.Payment,
                        ),
                        children: [
                            {
                                path: '*',
                                element: (
                                    <AsyncPage page={Pages.PaymentInvoices} />
                                ),
                            },
                        ],
                    },
                    {
                        path: routesHelper.getSubThreeRoute(
                            InvoicesSubRoutes.Payout,
                        ),
                        children: [
                            {
                                path: '*',
                                element: (
                                    <AsyncPage page={Pages.PayoutInvoices} />
                                ),
                            },
                        ],
                    },
                ],
            },
            {
                path: '*',
                element: <Navigate to={MainRoutes.home} />,
            },
            {
                path: MainRoutes.home,
                element: <Navigate to={balanceListRoute} />,
            },
        ],
    }),
]);

export default router;
